/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import App from 'apmui/App';
import EventTags from 'apmui/EventTags';
import { INPUT, IFormField } from 'apmui/components/form/Model';
import { CommonDialogAction, DialogClass, DialogType, IDialogConfiguration } from 'apmui/components/dialog/Model';
import * as Dialog from 'apmui/components/dialog/DialogHelper';
import {
    CustomProtocolHandler,
    IClientConfiguration,
    PROGRESS_STATUS,
    getCustomProtocolLink,
    scError,
    OS,
} from 'apmui/plugin';
import { UserAgent, userAgent } from 'apmui/util/UserAgent';
import deepCopy from 'apmui/util/deepCopy';
import IPageModel from './Model';

import 'apmui/components/dialog/Controller';
import 'apmui/components/form/Controller';
import { installActiveX, EVENT as AXEVENT, STATUS } from 'apmui/plugin/installActiveX';
import { COOKIE, getInternalCookie } from 'apmui/common/Cookie';
import { divLog } from 'apmui/util/divLog';
import { installMacPlugin, EVENT as MPEVENT } from 'apmui/plugin/installMacPlugin';

const INSTALLER_URL_FIELD = 'installerURL';
const EPS_INITIAL_STATUS = PROGRESS_STATUS.obtaining_status;

const app = App.get();

enum EpsDialogAction {
    DOWNLOAD = 'download_action',
    RELOAD = 'reload_action',
    RESTART = 'restart_action',
    SHOW_DOWNLOAD_SCREEN = 'install_action',
    SKIP = 'skip_action',
}

let g_CustomProtocolHandler: CustomProtocolHandler | null = null;

/* TODO: move to function */
const baseUrl = window.location.href.match(/(\w+:\/\/[^\/]+)/)![1];

app.subscribeDefault(EventTags.CONFIGURE, (_, page) => {
    if (page.pageType !== 'eps') {
        return;
    }
    const eps = page.eps;
    eps.state = {
        status: sepcGetLocalizedStatus(page, EPS_INITIAL_STATUS),
    };
});

app.subscribeDefault(EventTags.START, (_, page) => {
    if (page.pageType !== 'eps') {
        return;
    }
    const eps = page.eps;
    /*
     * client type is standalone or use native plugins is false, see definitinon in page.json
     */
    divLog(`client.customProtocol=${eps.client.customProtocol} .type=${eps.client.type},`);
    divLog(`.os=${eps.client.os}, components.os=${eps.components.os}`);
    if (eps.client.customProtocol === false && eps.components.os === OS.WINDOWS) {
        const sess = String(getInternalCookie(COOKIE.MRHSESSION));
        const packages = eps.components.packages;
        // Original UInstaller constructor is using this id instead
        packages.installerControl.id = 'CLSID:A1DB6596-BC73-4dd1-A84A-04428FA92D7D';

        const callback = (event: AXEVENT, arg1: string, arg2: string): void => {
            divLog(` *** axcallback - enter ${event}, '${arg1}', ` + String(arg2).substr(0, 10));

            switch (event) {
                case AXEVENT.INIT:
                    app.dispatch(EventTags.EPS_CHECK_PROGRESS, PROGRESS_STATUS.check_client);
                    break;
                case AXEVENT.INSPECTION_HOST_INSTALL:
                case AXEVENT.INSTALLER_CONTROL_RECHECK:
                case AXEVENT.INSTALLER_CONTROL_UPDATE:
                case AXEVENT.LEGACY_INSTALLER_CONTROL_UPGRADE:
                    if (arg1 === STATUS.SEQUENCE_FAILED || arg1 === STATUS.CREATE_FAILED) {
                        sepcOnCheckProgress(PROGRESS_STATUS.activex_required);
                    } else if (arg1 === STATUS.STARTED) {
                        sepcOnCheckProgress(PROGRESS_STATUS.installing_message);
                    } else if (arg1 === STATUS.DONE) {
                        sepcOnCheckProgress(PROGRESS_STATUS.done);
                    } else {
                        sepcOnCheckProgress(PROGRESS_STATUS.check_client);
                    }
                    break;
                case AXEVENT.CHANGE_TEXT:
                    sepcOnCheckProgress(arg1);
                    break;
                case AXEVENT.NO_INSPECTION_HOST:
                    sepcSkipInspection(page);
                    break;
                case AXEVENT.CHANGE_URL:
                    window.location.href = arg1;
                    break;
            }

            divLog(` *** axcallback - exit ${eps.state.status}`);
        };
        installActiveX(packages, sess, baseUrl, callback);
        return; // ATTENTION RETURN
    } else if (eps.client.customProtocol === false && eps.components.os === OS.MAC) {
        const packages = eps.components.packages;
        const sess = String(getInternalCookie(COOKIE.MRHSESSION));
        const callback = (event: MPEVENT, arg1: string, arg2: string): void => {
            divLog(` *** mpcallback - enter ${event}, '${arg1}', ` + arg2.substr(0, 10));

            switch (event) {
                case MPEVENT.INIT:
                    sepcOnCheckProgress(PROGRESS_STATUS.check_client);
                    break;
                case MPEVENT.INSPECTION_HOST_RUN:
                    sepcOnCheckProgress(PROGRESS_STATUS.installing_message);
                    break;
                case MPEVENT.INSPECTION_HOST_MAN_INSTALL:
                    sepcOnCheckProgress(PROGRESS_STATUS.restart_client_install);
                    break;
                case MPEVENT.CHANGE_TEXT:
                    sepcOnCheckProgress(arg1);
                    break;
                case MPEVENT.CHANGE_URL:
                    window.location.href = arg1;
                    break;
            }

            divLog(` *** mpcallback - exit ${eps.state.status}`);
        };
        installMacPlugin(packages, sess, baseUrl, callback);
        return; // ATTENTION RETURN
    }

    /* Normal client */
    const showMessage = (msg: DialogType) => () =>
        app
            .showModal(msg)
            .then((result) => {
                switch (result) {
                    case EpsDialogAction.RELOAD:
                        /* sepcReloadPage(); */
                        window.location.reload(true);
                        break;
                    case EpsDialogAction.RESTART:
                        /* sepcRestart(); */
                        if (g_CustomProtocolHandler) {
                            g_CustomProtocolHandler.Run(true);
                        }
                        break;
                    case EpsDialogAction.SHOW_DOWNLOAD_SCREEN:
                        showMessage(DialogType.EPS_INSTALL)();
                        break;
                }
            })
            .catch(() => {
                sepcSkipInspection(page);
            });
    g_CustomProtocolHandler = new CustomProtocolHandler({
        onAutoStartNotSupported: showMessage(DialogType.EPS_AUTO_START_NOT_SUPPORTED),
        onPopupBlockedIE: showMessage(DialogType.EPS_POPUP_BLOCKED),
        onNotInstalled: showMessage(DialogType.EPS_INSTALL),
        onUserDeclined: showMessage(DialogType.EPS_INSPECTION_HOST_NOT_CONNECTED),
        onConnectionTimeout: showMessage(DialogType.EPS_INSPECTION_HOST_NOT_CONNECTED),
        hideMessage() {
            app.closeModal({ dialog: DialogClass.ENDPOINT_SECURITY, resolve: true });
        },
        onDebugMsg: sepcDebug,
        browser: userAgent.family,
        link: sepcGetCustomProtocolLink(eps.components),
        token: 'eps',
        pollingEnabled: true,
        timeoutEnabled: true,
        edgeClientConfig: eps.client.edgeClientConfig,
        onHandlerDetected() {
            /* */
        },
        statusCheckOptions: {
            noConnectionTimeoutVal: 4000, // isCustomProtocolDetectionSupported() ? 4000 : 100,
            onComplete: sepcOnCheckComplete,
            onProgress: sepcOnCheckProgress,
            onError: sepcOnCheckFailure,
            uri: '/my.status.eps',
        },
    });
    /* Defer ProtocolHandler start until after initial render */
    setTimeout(() => {
        if (g_CustomProtocolHandler) {
            g_CustomProtocolHandler.Run(false);
        }
    }, 100);
    window.addEventListener('unload', () => {
        if (g_CustomProtocolHandler) {
            g_CustomProtocolHandler.Abort();
        }
    });
});

function initInstallerDialog(dialog: IDialogConfiguration, components: IClientConfiguration) {
    const downloadLink = Dialog.getAction(dialog, EpsDialogAction.DOWNLOAD);
    if (!downloadLink) {
        return;
    }
    if (components.os !== OS.LINUX) {
        return;
    }
    const installerURLs = components.installerURLs;
    const installerField: IFormField = {
        name: INSTALLER_URL_FIELD,
        type: INPUT.RADIO,
        caption: '',
        options: installerURLs.map((entry) => ({
            text: entry.id,
            value: entry.url,
        })),
        value: installerURLs[0].url,
    };

    Dialog.insertBefore(dialog, downloadLink, installerField);
}

function getInstallerUrl(dialog: IDialogConfiguration | null, components: IClientConfiguration): string {
    if (components.os !== OS.LINUX) {
        return components.installerURL;
    }
    const installerURLs = components.installerURLs;
    if (dialog) {
        const field = Dialog.getField(dialog, INSTALLER_URL_FIELD);
        if (field) {
            return field.value as string;
        }
    }
    return installerURLs[0].url;
}

app.subscribe(EventTags.DIALOG_OPEN, ({ detail }, page) => {
    if (page.pageType !== 'eps' || typeof detail.dialog !== 'string' || !(detail.dialog in page.eps.dialogs)) {
        return;
    }
    const dialogId = detail.dialog;
    detail.dialog = {
        ...deepCopy(page.eps.dialogs[dialogId]),
        id: dialogId,
        class: DialogClass.ENDPOINT_SECURITY,
    } as IDialogConfiguration;

    if (dialogId === DialogType.EPS_INSTALL) {
        initInstallerDialog(detail.dialog, page.eps.components);
    }
});

app.subscribe(EventTags.DIALOG_ACTION, (event, page) => {
    const detail = event.detail;
    if (page.pageType !== 'eps' || !detail.dialog || !(detail.dialog.id in page.eps.dialogs)) {
        return;
    }
    const dialog = Dialog.get(page, detail.dialog);

    switch (detail.action) {
        case EpsDialogAction.DOWNLOAD:
            window.open(getInstallerUrl(dialog, page.eps.components), '_self');
            event.preventDefault();
            break;
        case EpsDialogAction.SKIP:
            detail.action = CommonDialogAction.CANCEL;
            break;
    }
});

function sepcGetHangupUrl(errorCode?: number) {
    const defaultHangUpUrl = '/vdesk/hangup.php3';
    if (errorCode) {
        return defaultHangUpUrl + '?errorcode=' + errorCode;
    }
    return defaultHangUpUrl;
}

function GetServerUrl() {
    return window.location.protocol + '//' + window.location.host;
}

function sepcGoToNextPage(path: string) {
    const url = GetServerUrl() + path;
    // Mozila browser can not be redirected through javascript
    // set up a 3 sec watchdog and ask user to click on a link to continue
    //    setTimeout('continue_link_show("' + url + '")', 3000);

    window.open(url, '_self');
}

function sepcSkipInspection(page: IPageModel) {
    const FORM_ID = 'no_inspection_host_form';

    if (g_CustomProtocolHandler) {
        g_CustomProtocolHandler.Abort();
    }

    page[page.pageType].form = {
        id: FORM_ID,
        title: '',
        submitCaption: '',
        fields: [
            {
                caption: '',
                type: INPUT.HIDDEN,
                name: 'no-inspection-host',
                value: '1',
            },
        ],
    };

    app.submitForm(FORM_ID);
}

function sepcGetCustomProtocolLink(components: IClientConfiguration) {
    const dict = {};
    dict['server'] = window.location.hostname;
    if (components.os === OS.WINDOWS) {
        const packageList = components.packageList;
        for (let i = 0; i < packageList.length; i++) {
            const instance = components.packages[packageList[i]];
            dict['version' + i] = instance.version;
            dict['id' + i] = instance.id
                .trim()
                .replace(/CLSID:(.*)/, '{$1}')
                .replace(/ /g, '+');
            dict['package' + i] = instance.url;
        }
    }
    return getCustomProtocolLink(components.scheme, dict);
}

function sepcDebug(...args: string[]) {
    console.log(...args);
}

/*
 * Check progress
 */

function sepcOnCheckProgress(eventName?: string) {
    if (eventName && eventName.length > 0) {
        app.dispatch(EventTags.EPS_CHECK_PROGRESS, eventName);
    }
    // custom protocol handler application will send a token only in one case as of now -
    // when security zone for the server has changed on client side, through adding it to Trusted Site list.
    // The actual value of the token can be ignored for now
    if (eventName === PROGRESS_STATUS.token_value) {
        return false;
    }
    // two first updates are always "obtaining_status" and "awaiting_connection"
    // ignoring them because they don't indicate connected EPCheck module
    if (eventName === PROGRESS_STATUS.obtaining_status || eventName === PROGRESS_STATUS.awaiting_connection) {
        return false;
    }
    return true;
}

app.subscribe(EventTags.EPS_CHECK_PROGRESS, ({ detail: eventName }, page) => {
    if (page.pageType !== 'eps') {
        return;
    }
    // agent endpoint windows browser cache cleaner
    if (userAgent.family === UserAgent.MSIE && eventName == 'agent_endpoint_windows_browser_cache_cleaner') {
        sepcGoToNextPage('/my.policy');
        return;
    }

    page.eps.state.status = sepcGetLocalizedStatus(page, eventName);

    if (UserAgent.EDGE === userAgent.family) {
        if (eventName === PROGRESS_STATUS.obtaining_status || eventName === PROGRESS_STATUS.awaiting_connection) {
            // show additional instructions for Edge Browser
            page.eps.state.notification = page.eps.strings.edgeBrowserInstructionsMessage;
        } else if (page.eps.state.notification === page.eps.strings.edgeBrowserInstructionsMessage) {
            page.eps.state.notification = undefined;
        }

        if (eventName === PROGRESS_STATUS.token_value) {
            page.eps.state.needRestart = true;
        }
    }
});

function sepcGetLocalizedStatus(page: IPageModel, msgId: string): string {
    if (msgId in page.eps.strings) {
        return page.eps.strings[msgId];
    }

    // since msgId is agent id, it may contain platform identifier
    // but message id not always contains it, let's strip platform out
    const modifiedMsgId = msgId.replace(/(mac|windows|linux)_/, '');

    if (modifiedMsgId in page.eps.strings) {
        return page.eps.strings[modifiedMsgId];
    }

    return msgId;
}

/*
 * Check complete
 */

function sepcOnCheckComplete() {
    app.dispatch(EventTags.EPS_CHECK_COMPLETE);
}

app.subscribe(EventTags.EPS_CHECK_COMPLETE, (_, page) => {
    if (page.pageType !== 'eps') {
        return;
    }

    /* Check complete; hide progress indicatior */
    page.eps.state.status = undefined;

    if (page.eps.state.needRestart) {
        page.eps.state.text = page.eps.strings.openNewBrowserMessage;
    } else {
        setTimeout(() => sepcGoToNextPage('/my.policy?p=' + Math.floor(Math.random() * 1000000)), 1000);
    }
});

/*
 * Check failed
 */

function sepcOnCheckFailure(httpError = 0, apmError = 0, retriesLeft = 0) {
    app.dispatch(EventTags.EPS_CHECK_FAILED, {
        apmError,
        httpError,
        retriesLeft,
    });
}

app.subscribe(EventTags.EPS_CHECK_FAILED, (event) => {
    const { apmError, httpError, retriesLeft } = event.detail;

    sepcDebug(`sepcOnCheckFailure httpError=${httpError}, apmError=${apmError}`);

    if (apmError === scError.sessionExpired || httpError === 404) {
        sepcGoToNextPage(sepcGetHangupUrl(20)); // 20 - expired
        return;
    }

    if (retriesLeft > 0) {
        return;
    }

    let dialogId = DialogType.EPS_FAILED_TO_RETRIEVE_STATUS_UPDATE;

    if (apmError === scError.failedToParse) {
        dialogId = DialogType.EPS_FAILED_TO_PARSE_STATUS_UPDATE;
    }
    app.showModal(dialogId);
});
